/** Port of CommonUiMapperImpl's price/duration formatting.
 *
 * Prices, durations, dates and plurals are all composed on the device — the server
 * sends `cost: 131.0`, `cost_prefix: "From"`, `cost_unit: "Per person"`, `currency: 1`
 * and the client assembles "From $131". Get these wrong and the number on screen is
 * simply a different number.
 *
 * Spec §5.5.
 */

export interface Currency {
  id: number;
  symbol: string;
  /** SYMBOL_PRE_VALUE = "$120"; VALUE_PRE_SYMBOL = "120 kr"; anything else = "". */
  align: 'SYMBOL_PRE_VALUE' | 'VALUE_PRE_SYMBOL' | string;
}

export const STRINGS = {
  included: 'Included',
  free: 'Free',
  priceLevelSymbol: '$',
  from: 'From',
  upTo: 'Up to',
  timeH: 'h',
  timeM: 'm',
  averageTime: (t: string) => `Average time ${t}`,
  // note the PLAIN hyphen, not the en-dash Text.DASH uses elsewhere
  averageTimeRange: (a: string, b: string) => `Average time ${a} - ${b}`,
} as const;

/** `Text.DASH` — an en-dash with spaces either side. */
export const DASH = ' – ';

/**
 * mapCost — the core, and the easiest thing to get wrong.
 *
 *   roundedCost = cost.roundToInt()          HALF-UP, to a whole number
 *   if (roundedCost == 0) return "Free"      discards prefix AND unit
 *   ...no thousands separators, no decimals
 */
export function mapCost(
  currency: Currency | null | undefined,
  prefix: string | null | undefined,
  cost: number,
  unit?: string | null,
): string {
  const rounded = Math.round(cost);
  if (rounded === 0) return STRINGS.free;

  let s: string;
  if (currency) {
    if (currency.align === 'SYMBOL_PRE_VALUE') s = `${currency.symbol}${rounded}`;
    else if (currency.align === 'VALUE_PRE_SYMBOL') s = `${rounded} ${currency.symbol}`;
    else s = ''; // any other align appends nothing at all
  } else {
    s = String(rounded);
  }

  if (unit) s = `${s} ${unit}`;
  return prefix ? `${prefix} ${s}` : s;
}

/** A prefix with no cost is valid — e.g. "Enjoy 10% off". */
export function mapPrice(
  currency: Currency | null | undefined,
  prefix: string | null | undefined,
  cost: number | null | undefined,
  unit?: string | null,
): string | null {
  const prefixText = prefix?.trim() || null;
  if (cost === null || cost === undefined) return prefixText;
  return mapCost(currency, prefixText, cost, unit);
}

export function mapPriceRange(
  currency: Currency | null | undefined,
  prefix: string | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined,
  unit?: string | null,
): string | null {
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;

  if (hasMin && hasMax) {
    // no prefix or unit on the low end; unit only on the high end
    return `${mapCost(currency, null, min)}${DASH}${mapCost(currency, null, max, unit)}`;
  }
  if (hasMin) return mapCost(currency, prefix?.trim() || STRINGS.from, min, unit);
  if (hasMax) return mapCost(currency, prefix?.trim() || STRINGS.upTo, max, unit);
  return null;
}

export const PRICE_LEVEL_FREE = 0;

export function mapPriceLevel(level: number | null | undefined): string | null {
  if (level === null || level === undefined) return null;
  if (level === PRICE_LEVEL_FREE) return STRINGS.free;
  return STRINGS.priceLevelSymbol.repeat(level);
}

export const mapIncludedPrice = (): string => STRINGS.included;

/** "2h", "30m", "2h 30m" — zero components omitted, single space between. */
export function mapTime(hours: number, minutes: number): string {
  const parts: string[] = [];
  if (hours !== 0) parts.push(`${hours}${STRINGS.timeH}`);
  if (minutes !== 0) parts.push(`${minutes}${STRINGS.timeM}`);
  return parts.join(' ');
}

/** Durations arrive as fractional hours. */
export function durationToHm(duration: number): { hours: number; minutes: number } {
  const total = Math.round(duration * 60);
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/** `mapDuration` — the activity screen's `topLabel`. */
export function mapDuration(
  minDuration: number | null | undefined,
  maxDuration: number | null | undefined,
): string | null {
  const fmt = (d: number) => {
    const { hours, minutes } = durationToHm(d);
    return mapTime(hours, minutes);
  };
  const hasMin = minDuration !== null && minDuration !== undefined && minDuration !== 0;
  const hasMax = maxDuration !== null && maxDuration !== undefined && maxDuration !== 0;

  if (hasMin && hasMax) {
    return minDuration === maxDuration
      ? STRINGS.averageTime(fmt(minDuration))
      : STRINGS.averageTimeRange(fmt(minDuration), fmt(maxDuration));
  }
  if (hasMin) return STRINGS.averageTime(fmt(minDuration));
  if (hasMax) return STRINGS.averageTime(fmt(maxDuration));
  return null;
}

/** Android price/payment enums, as they appear on the wire. */
export const PAYMENT_TYPE = { COST_IS_INCLUDED: 0, PAY_AT_LOCATION: 1, PREPAID_ADDON: 2 } as const;
export const PRICE_TYPE = { HIDE: 0, PRICE_LEVEL: 1, EXACT_PRICE: 2, PRICE_RANGE: 3 } as const;

export interface ActivityPriceInput {
  paymentType: number | null | undefined;
  priceType: number | null | undefined;
  priceLevel: number | null | undefined;
  costMin: number | null | undefined;
  costMax: number | null | undefined;
  costPrefix: string | null | undefined;
  costUnit: string | null | undefined;
  currency: Currency | null | undefined;
}

/** `mapActivityPrice` — used for the bottom bar and the nearby-card caption. */
export function mapActivityPrice(a: ActivityPriceInput): string | null {
  if (a.paymentType === PAYMENT_TYPE.COST_IS_INCLUDED) return mapIncludedPrice();
  if (a.paymentType === null || a.paymentType === undefined) return null;

  switch (a.priceType) {
    case PRICE_TYPE.PRICE_LEVEL:
      return mapPriceLevel(a.priceLevel);
    case PRICE_TYPE.EXACT_PRICE:
      return mapPrice(a.currency, a.costPrefix, a.costMin, a.costUnit);
    case PRICE_TYPE.PRICE_RANGE:
      // prefix forced null on the range path
      return mapPriceRange(a.currency, null, a.costMin, a.costMax, a.costUnit);
    default:
      return null;
  }
}

/**
 * The bottom bar drops the unit in several cases — mirrors `mapBottomModel`.
 */
export function bottomBarUnit(a: ActivityPriceInput, price: string | null): string | null {
  if (price === null) return null;
  if (a.paymentType === PAYMENT_TYPE.COST_IS_INCLUDED) return null;
  if (a.priceType === PRICE_TYPE.PRICE_LEVEL) return null;
  if (
    a.priceType === PRICE_TYPE.EXACT_PRICE &&
    (a.costMin === null || a.costMin === undefined || Math.round(a.costMin) === 0)
  ) {
    return null;
  }
  return a.costUnit?.trim() || null;
}
